import { createContext, MutableRefObject } from 'react';
import * as d3Scale from 'd3-scale';
import { Transition } from 'framer-motion';
import { Scale } from './Types';

export interface Props {
  /**
   * The uniquely generated ID associated with the visualisation. This
   * is used to namespace the SVG definitions.
   */
  artboardId: string;
  /**
   * The relative number of pixels from the bottom of the artboard area
   * to the bottom of the visualisation container.
   */
  artboardBottom: number;
  /**
   * The number of pixels from the top of the artboard area to the
   * bottom of the artboard area.
   */
  artboardHeight: number;
  /**
   * The relative number of pixels from the left of the artboard area
   * to the left of the visualisation container.
   */
  artboardLeft: number;
  /**
   * The relative number of pixels from the right of the artboard area
   * to the right of the visualisation container.
   */
  artboardRight: number;
  /**
   * The relative number of pixels from the top of the artboard area
   * to the top of the visualisation container.
   */
  artboardTop: number;
  /**
   * The number of pixels from the left of the artboard area to the
   * right of the artboard area.
   */
  artboardWidth: number;
  /**
   * The definition ID for the clip mask where the bounding box is the
   * artboard.
   */
   defArtboardClipMaskId: string;
  /**
   * A function to convert a relative x axis pixel value on the artboard to
   * a domain x value.
   */
  getDomainX: (x: number) => number;
  /**
   * A function to convert a relative y axis pixel value on the artboard to
   * a domain y value.
   */
  getDomainY: (y: number) => number;
  /**
   * A function to convert a domain x axis value to a relative x pixel value
   * on the artboard.
   getX: (x: number) => number;
   */
  getX: (x: null | number) => null | number;
  /**
   * A function to convert a domain x0 axis value to a relative x pixel value
   * on the artboard.
   */
  getX0: (x: undefined | null | number) => null | number;
  /**
   * A function to convert a domain y axis value to a relative y pixel value
   * on the artboard.
   */
  getY: (y: null | number) => null | number;
  /**
   * A function to convert a domain y0 axis value to a relative y pixel value
   * on the artboard.
   */
  getY0: (y: undefined | null | number) => null | number;
  /**
   * The number of pixels from the top of the visualisation container to the bottom
   * of the visualisation.
   */
  height: number;
  /**
   * A React ref object for the visualisation container.
   */
  ref: MutableRefObject<HTMLDivElement | null>;
  /**
   * Transition to be used on all viz animations.
   * See Framer Motion's Transition object.
   */
  transition?: Transition;
  /**
   * The number of pixels from the left of the visualisation container to the right
   * of the visualisation.
   */
  width: number;
  /** The lower and upper bounds of the x series domain. */
  xDomain: number[];
  /** The d3Scale that is being used to position points across the X axis */
  xScale: Scale;
  /** The lower and upper bounds of the y series domain. */
  yDomain: number[];
  /** The d3Scale that is being used to position points across the Y axis */
  yScale: Scale;
}

/**
 * Context for communicating information about the size of the visualisation
 * and domain of the data to help with plotting series. This can be used
 * for creating custom series with the useContext hook.
 **/
const VizContext = createContext<Props>({
  artboardId: '',
  artboardBottom: 0,
  artboardLeft: 0,
  artboardRight: 0,
  artboardTop: 0,
  artboardWidth: 0,
  artboardHeight: 0,
  defArtboardClipMaskId: '',
  getDomainX: () => 0,
  getDomainY: () => 0,
  getX: () => 0,
  getX0: () => 0,
  getY: () => 0,
  getY0: () => 0,
  height: 0,
  ref: { current: null },
  width: 0,
  xDomain: [],
  xScale: d3Scale.scaleLinear(),
  yDomain: [],
  yScale: d3Scale.scaleLinear(),
});

export default VizContext;
